import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import CommandLayout from "@/components/CommandLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  ArrowLeft, Loader2, Activity, Film, Clock, Radio, GitCompareArrows,
  Terminal, AlertTriangle, Play, Download, CheckCircle2,
} from "lucide-react";
import { RenderPreview } from "@/remotion/RenderPreview";
import { selectTemplate, planTimeline } from "@/remotion";
import { getStatusColor, type ContentStatus } from "@/lib/contentLifecycle";
import {
  generateSimulationContent,
  type ContentFormat,
  type SimulationContent,
  type DialogueTurn,
} from "@/lib/contentGenerator";
import {
  calculateEngagementScore,
  engagementBand,
  syntheticBaseline,
  type AssetMetricsRow,
} from "@/lib/contentMetrics";

interface AssetRow {
  id: string;
  hook: string | null;
  scenario_id: string;
  scenario_title: string;
  archetype: string;
  category: string;
  format: string;
  platform: string;
  status: string;
  render_status: string | null;
  cta: string | null;
  created_at: string;
  updated_at?: string | null;
}

interface MetricsRow extends Partial<AssetMetricsRow> {
  content_asset_id?: string;
}

interface EventRow {
  id: string;
  event_type: string;
  created_at: string;
  metadata: Record<string, unknown> | null;
  timestamp_ms: number | null;
}

interface FormatBenchmarkRow {
  format: string;
  avg_engagement: number;
  avg_completion: number;
  avg_exports: number;
  count: number;
}

const FMT_LABEL: Record<string, string> = {
  spiral: "Spiral",
  replay: "Replay",
  pov: "POV",
};

export default function AdminAssetDetail() {
  const { id } = useParams<{ id: string }>();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [asset, setAsset] = useState<AssetRow | null>(null);
  const [metrics, setMetrics] = useState<MetricsRow | null>(null);
  const [events, setEvents] = useState<EventRow[]>([]);
  const [allMetrics, setAllMetrics] = useState<
    Array<{ content_asset_id: string; format: string } & MetricsRow>
  >([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Auth gate
  useEffect(() => {
    if (authLoading) return;
    if (!user) navigate("/", { replace: true });
  }, [user, authLoading, navigate]);

  // Load
  useEffect(() => {
    if (!id || !user) return;
    let active = true;
    (async () => {
      setLoading(true);
      const [aRes, mRes, eRes, allRes] = await Promise.all([
        (supabase as any)
          .from("content_assets")
          .select("id, hook, scenario_id, scenario_title, archetype, category, format, platform, status, render_status, cta, created_at, updated_at")
          .eq("id", id)
          .maybeSingle(),
        (supabase as any)
          .from("content_metrics")
          .select("*")
          .eq("content_asset_id", id)
          .maybeSingle(),
        (supabase as any)
          .from("content_events")
          .select("id, event_type, created_at, metadata, timestamp_ms")
          .eq("content_asset_id", id)
          .order("created_at", { ascending: false })
          .limit(50),
        (supabase as any)
          .from("content_assets")
          .select("id, format, content_metrics(*)")
          .limit(500),
      ]);
      if (!active) return;
      if (aRes.error || !aRes.data) { setNotFound(true); setLoading(false); return; }
      setAsset(aRes.data as AssetRow);
      setMetrics((mRes?.data ?? null) as MetricsRow | null);
      setEvents((eRes?.data ?? []) as EventRow[]);

      // Flatten benchmark dataset
      const flat: Array<{ content_asset_id: string; format: string } & MetricsRow> = [];
      for (const r of (allRes?.data ?? []) as any[]) {
        const m = Array.isArray(r.content_metrics) ? r.content_metrics[0] : r.content_metrics;
        if (m) flat.push({ content_asset_id: r.id, format: r.format, ...m });
      }
      setAllMetrics(flat);
      setLoading(false);
    })();
    return () => { active = false; };
  }, [id, user]);

  // Effective metrics — real if present, otherwise deterministic baseline
  const eff = useMemo<MetricsRow>(() => {
    if (!asset) return {};
    const base = syntheticBaseline(asset.id);
    const merged: MetricsRow = { ...base, ...(metrics ?? {}) };
    if (!merged.engagement_score) {
      merged.engagement_score = calculateEngagementScore(merged);
    }
    return merged;
  }, [asset, metrics]);

  // Dialogue / timeline regeneration (deterministic from scenario+format)
  const sim = useMemo<SimulationContent | null>(() => {
    if (!asset) return null;
    try {
      return generateSimulationContent({
        scenarioId: asset.scenario_id,
        format: asset.format as ContentFormat,
      });
    } catch {
      return null;
    }
  }, [asset]);

  if (authLoading || loading) {
    return (
      <CommandLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-cyan" />
        </div>
      </CommandLayout>
    );
  }

  if (notFound || !asset) {
    return (
      <CommandLayout>
        <div className="max-w-3xl mx-auto py-16 text-center space-y-4">
          <AlertTriangle className="w-8 h-8 mx-auto text-amber-400" />
          <h1 className="text-xl font-mono uppercase tracking-wider">Asset not found</h1>
          <p className="text-sm text-muted-foreground">
            This intelligence dossier is unavailable or has been purged.
          </p>
          <Button asChild variant="outline" className="border-glass-border">
            <Link to="/admin"><ArrowLeft className="w-4 h-4 mr-2" />Return to console</Link>
          </Button>
        </div>
      </CommandLayout>
    );
  }

  const score = Number(eff.engagement_score ?? 0);
  const band = engagementBand(score);

  return (
    <CommandLayout>
      <div className="space-y-6 pb-12">
        {/* Top bar */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground">
            <span className="text-cyan">Admin</span> /
            <span>Viral</span> /
            <span>Assets</span> /
            <span className="text-foreground/80">{asset.id.slice(0, 8)}</span>
          </div>
          <Button asChild variant="outline" size="sm" className="border-glass-border text-muted-foreground hover:text-foreground">
            <Link to="/admin"><ArrowLeft className="w-4 h-4 mr-2" />Back to console</Link>
          </Button>
        </div>

        {/* Header dossier */}
        <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
          <CardContent className="p-5 md:p-6 space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="outline" className={`text-[10px] uppercase tracking-wider font-mono ${getStatusColor(asset.status as ContentStatus)}`}>
                {asset.status}
              </Badge>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider font-mono border-glass-border text-foreground/80">
                {FMT_LABEL[asset.format] ?? asset.format}
              </Badge>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider font-mono border-glass-border text-muted-foreground">
                {asset.platform}
              </Badge>
              <Badge variant="outline" className="text-[10px] uppercase tracking-wider font-mono border-violet-400/40 text-violet-300">
                {asset.archetype}
              </Badge>
              <span className="ml-auto text-[10px] font-mono text-muted-foreground">
                {new Date(asset.created_at).toLocaleString()}
              </span>
            </div>

            <h1 className="text-xl md:text-2xl font-semibold leading-snug text-foreground">
              {asset.hook || "—"}
            </h1>
            <p className="text-sm font-mono text-muted-foreground">
              {asset.scenario_title}
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 pt-3 border-t border-glass-border">
              <Stat label="Engagement" value={String(score)} accent={bandClass(band)} sub={band.toUpperCase()} />
              <Stat label="Render Status" value={(asset.render_status ?? "pending")} />
              <Stat label="Category" value={asset.category} />
              <Stat label="Asset ID" value={asset.id.slice(0, 8)} mono />
            </div>
          </CardContent>
        </Card>

        {/* Behavioral Flow */}
        <Section title="Behavioral Flow" icon={<Activity className="w-4 h-4 text-cyan" />}>
          {sim && sim.dialogue.length > 0 ? (
            <BehavioralFlow turns={sim.dialogue} spiralIndex={sim.spiralMoment.turnIndex} betterResponse={sim.betterResponse} />
          ) : (
            <EmptyMono text="Dialogue unavailable for this asset." />
          )}
        </Section>

        {/* Timeline Intelligence */}
        <Section title="Timeline Intelligence" icon={<Clock className="w-4 h-4 text-cyan" />}>
          {sim ? (
            <TimelineStrip turns={sim.dialogue} ctaMs={sim.remotion.cta.durationMs} totalMs={sim.remotion.totalMs} spiralIndex={sim.spiralMoment.turnIndex} />
          ) : (
            <EmptyMono text="Timeline data unavailable." />
          )}
        </Section>

        {/* Performance Telemetry */}
        <Section title="Performance Telemetry" icon={<Radio className="w-4 h-4 text-cyan" />}>
          <TelemetryGrid m={eff} score={score} band={band} hasReal={!!metrics} />
        </Section>

        {/* Format Intelligence */}
        <Section title="Format Intelligence" icon={<GitCompareArrows className="w-4 h-4 text-cyan" />}>
          <FormatComparison currentFormat={asset.format} current={eff} all={allMetrics} />
        </Section>

        {/* Render Surface */}
        <Section title="Render Surface" icon={<Film className="w-4 h-4 text-cyan" />}>
          <RenderSurface asset={asset} sim={sim} />
        </Section>

        {/* Operational History */}
        <Section title="Operational History" icon={<Terminal className="w-4 h-4 text-cyan" />}>
          <EventLog events={events} createdAt={asset.created_at} />
        </Section>
      </div>
    </CommandLayout>
  );
}

// ============== Sub-components ==============

function Section({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Card className="bg-charcoal-medium/60 border-glass-border backdrop-blur-sm">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-mono uppercase tracking-[0.2em] flex items-center gap-2 text-foreground/90">
          {icon}{title}
        </CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

function Stat({ label, value, sub, accent, mono }: { label: string; value: string; sub?: string; accent?: string; mono?: boolean }) {
  return (
    <div className="space-y-1">
      <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-muted-foreground">{label}</div>
      <div className={`text-lg ${mono ? "font-mono" : "font-semibold"} ${accent ?? "text-foreground"}`}>{value}</div>
      {sub && <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{sub}</div>}
    </div>
  );
}

function EmptyMono({ text }: { text: string }) {
  return <p className="text-xs font-mono text-muted-foreground">{text}</p>;
}

function bandClass(b: "low" | "medium" | "high") {
  if (b === "high") return "text-emerald-300";
  if (b === "medium") return "text-amber-300";
  return "text-muted-foreground";
}

// ----- Behavioral Flow -----

function BehavioralFlow({ turns, spiralIndex, betterResponse }: { turns: DialogueTurn[]; spiralIndex: number; betterResponse: string }) {
  return (
    <div className="space-y-2">
      {turns.map((t) => {
        const isThem = t.role === "them";
        const isAlt = t.role === "you_alt";
        const isSpiral = t.index === spiralIndex;
        return (
          <div
            key={t.index}
            className={`flex gap-3 px-3 py-2 border-l-2 ${
              isThem ? "border-red-400/60 bg-red-500/5"
                : isAlt ? "border-emerald-400/60 bg-emerald-400/5"
                : "border-cyan/60 bg-cyan/5"
            } ${isSpiral ? "ring-1 ring-amber-400/40" : ""}`}
          >
            <div className="w-16 shrink-0 text-[10px] font-mono uppercase tracking-wider text-muted-foreground pt-0.5">
              <div>T{String(t.index).padStart(2, "0")}</div>
              <div className={isThem ? "text-red-300/80" : isAlt ? "text-emerald-300/80" : "text-cyan/80"}>
                {isThem ? "Them" : isAlt ? "You·alt" : "You"}
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm text-foreground/90 leading-snug">{t.text}</p>
              {isSpiral && (
                <p className="mt-1 text-[10px] font-mono uppercase tracking-wider text-amber-300/90">
                  ▲ Spiral · turning point
                </p>
              )}
            </div>
          </div>
        );
      })}
      <div className="mt-4 border-t border-glass-border pt-3">
        <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-cyan mb-1.5">Better Response</div>
        <p className="text-sm font-mono text-emerald-200/90 italic leading-snug">{betterResponse}</p>
      </div>
    </div>
  );
}

// ----- Timeline Intelligence -----

function TimelineStrip({ turns, ctaMs, totalMs, spiralIndex }: { turns: DialogueTurn[]; ctaMs: number; totalMs: number; spiralIndex: number }) {
  const segments = turns.map((t) => ({
    label: `T${t.index}`,
    role: t.role,
    duration: t.durationMs,
    pause: t.pauseAfterMs,
    spiral: t.index === spiralIndex,
  }));
  const total = totalMs || segments.reduce((s, x) => s + x.duration + x.pause, 0) + ctaMs;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-1 w-full h-6 rounded-sm overflow-hidden border border-glass-border bg-background/40">
        {segments.map((s, i) => {
          const w = ((s.duration + s.pause) / total) * 100;
          const color =
            s.role === "them" ? "bg-red-400/40"
              : s.role === "you_alt" ? "bg-emerald-400/40"
                : "bg-cyan/40";
          return (
            <div
              key={i}
              style={{ width: `${w}%` }}
              className={`h-full ${color} ${s.spiral ? "ring-1 ring-amber-400/70" : ""}`}
              title={`${s.label} · ${s.duration}ms (+${s.pause}ms pause)`}
            />
          );
        })}
        <div
          style={{ width: `${(ctaMs / total) * 100}%` }}
          className="h-full bg-violet-400/40"
          title={`CTA · ${ctaMs}ms`}
        />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono">
        {segments.map((s, i) => (
          <div key={i} className="border border-glass-border rounded-sm px-2 py-1.5 bg-background/30">
            <div className="flex items-center justify-between text-muted-foreground">
              <span className="uppercase tracking-wider">{s.label}</span>
              <span className={s.role === "them" ? "text-red-300/80" : s.role === "you_alt" ? "text-emerald-300/80" : "text-cyan/80"}>
                {s.role === "them" ? "Them" : s.role === "you_alt" ? "You·alt" : "You"}
              </span>
            </div>
            <div className="text-foreground/90">{s.duration}ms</div>
            <div className="text-muted-foreground">+{s.pause}ms pause{s.spiral ? " · spiral" : ""}</div>
          </div>
        ))}
        <div className="border border-violet-400/30 rounded-sm px-2 py-1.5 bg-violet-400/5">
          <div className="flex items-center justify-between text-violet-300/80">
            <span className="uppercase tracking-wider">CTA</span>
            <span>End</span>
          </div>
          <div className="text-foreground/90">{ctaMs}ms</div>
          <div className="text-muted-foreground">terminal frame</div>
        </div>
      </div>

      <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
        Total runtime · {(total / 1000).toFixed(2)}s
      </div>
    </div>
  );
}

// ----- Telemetry -----

function TelemetryGrid({ m, score, band, hasReal }: { m: MetricsRow; score: number; band: "low" | "medium" | "high"; hasReal: boolean }) {
  const cells: Array<{ label: string; value: number | string; suffix?: string }> = [
    { label: "Views", value: m.views ?? 0 },
    { label: "Impressions", value: m.impressions ?? 0 },
    { label: "Saves", value: m.saves ?? 0 },
    { label: "Shares", value: m.shares ?? 0 },
    { label: "Exports", value: m.exports ?? 0 },
    { label: "Copied", value: m.copied_responses ?? 0 },
    { label: "Completion", value: Math.round(Number(m.completion_rate ?? 0)), suffix: "%" },
    { label: "Engagement", value: score, suffix: "/100" },
  ];
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        {cells.map((c) => (
          <div key={c.label} className="border border-glass-border rounded-sm px-3 py-2 bg-background/30">
            <div className="text-[10px] uppercase tracking-[0.2em] font-mono text-muted-foreground">{c.label}</div>
            <div className="text-base font-mono text-foreground">
              {typeof c.value === "number" ? c.value.toLocaleString() : c.value}{c.suffix ?? ""}
            </div>
          </div>
        ))}
      </div>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <Badge variant="outline" className={`text-[10px] uppercase tracking-wider font-mono ${
          band === "high" ? "border-emerald-400/50 text-emerald-300 bg-emerald-400/10"
            : band === "medium" ? "border-amber-400/50 text-amber-300 bg-amber-400/10"
              : "border-muted-foreground/40 text-muted-foreground bg-muted-foreground/10"
        }`}>
          Signal · {band}
        </Badge>
        <span className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
          {hasReal ? "Live telemetry" : "Cold-start baseline · awaiting traffic"}
        </span>
      </div>
    </div>
  );
}

// ----- Format Intelligence -----

function FormatComparison({ currentFormat, current, all }: { currentFormat: string; current: MetricsRow; all: Array<{ format: string } & MetricsRow> }) {
  const formats = ["spiral", "replay", "pov"] as const;
  const avgs = formats.map((f) => {
    const subset = all.filter((x) => x.format === f);
    const n = subset.length || 1;
    return {
      format: f,
      engagement: subset.reduce((s, x) => s + Number(x.engagement_score ?? 0), 0) / n,
      completion: subset.reduce((s, x) => s + Number(x.completion_rate ?? 0), 0) / n,
      exports: subset.reduce((s, x) => s + Number(x.exports ?? 0), 0) / n,
      count: subset.length,
    };
  });

  const curEng = Number(current.engagement_score ?? 0);
  const curComp = Number(current.completion_rate ?? 0);
  const curExp = Number(current.exports ?? 0);

  return (
    <div className="space-y-2">
      <div className="grid grid-cols-3 gap-2">
        {avgs.map((a) => {
          const isCurrent = a.format === currentFormat;
          const dEng = curEng - a.engagement;
          const dComp = curComp - a.completion;
          const dExp = curExp - a.exports;
          return (
            <div
              key={a.format}
              className={`border rounded-sm px-3 py-2 ${isCurrent ? "border-cyan/50 bg-cyan/5" : "border-glass-border bg-background/30"}`}
            >
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-[10px] uppercase tracking-[0.2em] font-mono text-foreground/80">
                  {FMT_LABEL[a.format]}
                </span>
                <span className="text-[10px] font-mono text-muted-foreground">n={a.count}</span>
              </div>
              <DeltaRow label="Engagement" base={a.engagement} delta={isCurrent ? dEng : 0} showDelta={isCurrent} />
              <DeltaRow label="Completion" base={a.completion} delta={isCurrent ? dComp : 0} showDelta={isCurrent} suffix="%" />
              <DeltaRow label="Exports" base={a.exports} delta={isCurrent ? dExp : 0} showDelta={isCurrent} />
            </div>
          );
        })}
      </div>
      <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
        Deltas measured against same-format peer average.
      </p>
    </div>
  );
}

function DeltaRow({ label, base, delta, showDelta, suffix }: { label: string; base: number; delta: number; showDelta: boolean; suffix?: string }) {
  const positive = delta >= 0;
  return (
    <div className="flex items-center justify-between text-[11px] font-mono py-0.5">
      <span className="text-muted-foreground">{label}</span>
      <span className="text-foreground/90">
        {base.toFixed(1)}{suffix ?? ""}
        {showDelta && (
          <span className={`ml-2 ${positive ? "text-emerald-300" : "text-red-400"}`}>
            {positive ? "+" : ""}{delta.toFixed(1)}{suffix ?? ""}
          </span>
        )}
      </span>
    </div>
  );
}

// ----- Render Surface -----

function RenderSurface({ asset, sim }: { asset: AssetRow; sim: SimulationContent | null }) {
  const [renderStatus, setRenderStatus] = useState<string>(asset.render_status ?? "pending");
  const [renderUrl, setRenderUrl] = useState<string | null>((asset as any).render_url ?? null);
  const [renderDurationMs, setRenderDurationMs] = useState<number | null>((asset as any).render_duration_ms ?? null);
  const [progress, setProgress] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const tmpl = sim ? selectTemplate(asset.format).key : "spiral";
  const plan = useMemo(() => (sim ? planTimeline(sim) : null), [sim]);
  const totalSec = plan ? plan.totalFrames / plan.fps : 0;

  async function handleRender() {
    if (!sim || busy) return;
    setBusy(true);
    setError(null);
    setProgress(0);
    setRenderStatus("rendering");

    // Update DB → rendering
    await (supabase as any)
      .from("content_assets")
      .update({ render_status: "rendering", render_template: tmpl, updated_at: new Date().toISOString() })
      .eq("id", asset.id);
    void (supabase as any).from("content_events").insert({
      content_asset_id: asset.id,
      event_type: "render_started",
      metadata: { template: tmpl, total_frames: plan?.totalFrames ?? null },
    });

    const startedAt = Date.now();
    // Simulated cinematic render progression — pacing matches the
    // composition runtime so the operator gets a true feel for cost.
    const totalMs = Math.max(2400, Math.min(9000, totalSec * 600));
    const tick = 80;
    await new Promise<void>((resolve) => {
      const t0 = Date.now();
      const interval = setInterval(() => {
        const p = Math.min(100, ((Date.now() - t0) / totalMs) * 100);
        setProgress(p);
        if (p >= 100) {
          clearInterval(interval);
          resolve();
        }
      }, tick);
    });

    const elapsed = Date.now() - startedAt;
    // V1: in-browser cinematic preview is the render surface. The
    // produced "artifact" pointer is the asset's own preview route —
    // CLI MP4 emission plugs in here without UI changes.
    const placeholderUrl = `remotion://local/${asset.id}.mp4`;

    const { error: upErr } = await (supabase as any)
      .from("content_assets")
      .update({
        render_status: "rendered",
        render_url: placeholderUrl,
        render_duration_ms: elapsed,
        render_template: tmpl,
        rendered_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      })
      .eq("id", asset.id);

    if (upErr) {
      setError(upErr.message);
      setRenderStatus("failed");
      void (supabase as any).from("content_events").insert({
        content_asset_id: asset.id,
        event_type: "render_failed",
        metadata: { template: tmpl, error: upErr.message },
      });
    } else {
      setRenderUrl(placeholderUrl);
      setRenderDurationMs(elapsed);
      setRenderStatus("rendered");
      void (supabase as any).from("content_events").insert({
        content_asset_id: asset.id,
        event_type: "render_completed",
        metadata: { template: tmpl, duration_ms: elapsed, total_frames: plan?.totalFrames ?? null },
      });
    }
    setBusy(false);
  }

  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-[auto_1fr] gap-5 items-start">
        {/* Cinematic preview */}
        <div className="relative">
          {sim ? (
            <RenderPreview sim={sim} format={asset.format} />
          ) : (
            <div className="aspect-[9/16] w-[220px] border border-glass-border bg-background/40 rounded-sm flex items-center justify-center">
              <span className="text-[10px] font-mono text-muted-foreground">No timeline</span>
            </div>
          )}
          <div className="absolute top-2 left-2 z-10">
            <Badge variant="outline" className={`text-[9px] uppercase tracking-wider font-mono ${getStatusColor(renderStatus as any)}`}>
              {renderStatus}
            </Badge>
          </div>
          <div className="absolute top-2 right-2 z-10">
            <Badge variant="outline" className="text-[9px] uppercase tracking-wider font-mono border-cyan/40 text-cyan bg-cyan/5">
              {tmpl}
            </Badge>
          </div>
        </div>

        {/* Render controls + telemetry */}
        <div className="space-y-3">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono">
            <Spec label="Aspect" value="9:16" />
            <Spec label="FPS" value={String(plan?.fps ?? 30)} />
            <Spec label="Duration" value={plan ? `${totalSec.toFixed(2)}s` : "—"} />
            <Spec label="Frames" value={plan ? String(plan.totalFrames) : "—"} />
            <Spec label="Template" value={tmpl} />
            <Spec label="Render" value={renderStatus} />
            <Spec
              label="Render time"
              value={renderDurationMs ? `${(renderDurationMs / 1000).toFixed(2)}s` : "—"}
            />
            <Spec label="Resolution" value="1080×1920" />
          </div>

          {busy && (
            <div className="space-y-1.5">
              <div className="flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                <span className="text-cyan animate-pulse">▲ Rendering · {tmpl} composition</span>
                <span>{progress.toFixed(0)}%</span>
              </div>
              <div className="h-1 w-full bg-background/60 border border-glass-border rounded-sm overflow-hidden">
                <div
                  className="h-full bg-cyan transition-all"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {error && (
            <div className="text-[11px] font-mono text-red-400 border border-red-500/30 bg-red-500/5 px-3 py-2 rounded-sm">
              Render failed · {error}
            </div>
          )}

          {renderStatus === "rendered" && !busy && (
            <div className="flex items-center gap-2 text-[11px] font-mono text-emerald-300">
              <CheckCircle2 className="w-3.5 h-3.5" />
              Cinematic render complete · {tmpl} · {(renderDurationMs ? renderDurationMs / 1000 : 0).toFixed(2)}s
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-1">
            <Button
              size="sm"
              onClick={handleRender}
              disabled={busy || !sim}
              className="bg-cyan/15 hover:bg-cyan/25 text-cyan border border-cyan/40 font-mono uppercase tracking-wider text-[11px]"
            >
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}
              {renderStatus === "rendered" ? "Re-render Asset" : "Render Asset"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={renderStatus !== "rendered" || !renderUrl}
              className="border-glass-border font-mono uppercase tracking-wider text-[11px]"
              onClick={() => {
                if (!renderUrl) return;
                void (supabase as any).from("content_events").insert({
                  content_asset_id: asset.id,
                  event_type: "render_export_requested",
                  metadata: { url: renderUrl, template: tmpl },
                });
              }}
            >
              <Download className="w-3.5 h-3.5" />
              Export MP4
            </Button>
          </div>

          <p className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
            V1 · Local cinematic render only · No cloud · No scheduling · No voice
          </p>
        </div>
      </div>
    </div>
  );
}

function Spec({ label, value }: { label: string; value: string }) {
  return (
    <div className="border border-glass-border rounded-sm px-2 py-1.5 bg-background/30">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-foreground/90">{value}</div>
    </div>
  );
}

// ----- Event log -----

function EventLog({ events, createdAt }: { events: EventRow[]; createdAt: string }) {
  if (events.length === 0) {
    return (
      <div className="font-mono text-[11px] text-muted-foreground space-y-1">
        <div>[{new Date(createdAt).toISOString()}] asset_created</div>
        <div className="text-muted-foreground/60">No further operational events recorded.</div>
      </div>
    );
  }
  return (
    <ScrollArea className="h-[320px] pr-3">
      <div className="font-mono text-[11px] space-y-1">
        {events.map((e) => {
          const meta = e.metadata ?? {};
          const from = (meta as any).from;
          const to = (meta as any).to;
          const desc =
            e.event_type === "status_changed" && from && to
              ? `status_changed  ${from} → ${to}`
              : e.event_type;
          return (
            <div key={e.id} className="flex gap-3 border-b border-glass-border/40 py-1">
              <span className="text-muted-foreground shrink-0">
                [{new Date(e.created_at).toISOString().replace("T", " ").slice(0, 19)}]
              </span>
              <span className="text-cyan/80">{desc}</span>
            </div>
          );
        })}
      </div>
    </ScrollArea>
  );
}
